<template>
  <ul class="list-disc px-4 ml-4">
    <li
      v-for="field in mappedFields"
      :key="field.path"
      v-show="
        (field.isHidden === undefined || !field.isHidden.value) &&
        hasCorrectSource(field)
      "
    >
      <div class="flex gap-2 items-center">
        <span :title="getSourceDisplayInformation(field.path)">
          {{ getLabel(field) }}: {{ getFormattedValue(field) }}
        </span>

        <InfoIcon v-if="data[field.path].hint !== null">
          {{ data[field.path].hint }}
        </InfoIcon>
      </div>
    </li>
  </ul>
</template>

<script lang="ts" setup>
import {
  INPUT_MAPPING_SOURCE,
  InputMappingListProps,
} from '@/features/InputMapping/types';
import type { ArrayElement } from '@/types';
import { useForm } from '@/utils/form/useForm';
import { useInputMappingStore } from '@/features/InputMapping/store';
import { computed } from 'vue';
import InfoIcon from '@/components/global/InfoIcon/InfoIcon.vue';

const props = defineProps<InputMappingListProps>();

const { mappedPaths, getSourceDisplayInformation, data, getValue } =
  useInputMappingStore();

const { labelByPath } = useForm();

const mappedFields = computed(() =>
  props.fields.filter((field) => mappedPaths.includes(field.path)),
);

const hasCorrectSource = (
  field: ArrayElement<InputMappingListProps['fields']>,
) => {
  const allowedSources = field.allowedSources ?? [INPUT_MAPPING_SOURCE.GDV];
  return allowedSources.includes(data[field.path].source);
};

const getLabel = (
  field: ArrayElement<InputMappingListProps['fields']>,
): string => {
  if (field.label !== undefined) {
    return field.label;
  }

  return labelByPath.get(field.path)?.value ?? field.path;
};

const getFormattedValue = (
  field: ArrayElement<InputMappingListProps['fields']>,
): string => {
  let val = getValue(field.path);

  if (field.formatter !== undefined) {
    val = field.formatter(val);
  }

  if (field.suffix !== undefined) {
    val += ` ${field.suffix}`;
  }

  return data[field.path].valueReplacementText ?? val;
};
</script>
